import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckSquare, Plus, X, Settings, Pencil, Trash2 } from 'lucide-react';
import { checklistsApi, checklistSubtasksApi, ChecklistTemplate, ChecklistItemRule, ChecklistItemSubtask } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useTelegram } from '../hooks/useTelegram';
import { format, startOfToday } from 'date-fns';

type RuleForm = {
  text: string;
  recurrence: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'ONCE' | 'NO_DATE';
  scheduleParams?: any;
  duePolicy?: any;
  requiresApproval: boolean;
  startDate?: string;
  endDate?: string; // Дата окончания (дедлайн)
  hasDate: boolean; // Для задач без даты
  isPermanent?: boolean; // Постоянно (без даты окончания)
  time?: string; // Время выполнения (для DAILY, WEEKLY, MONTHLY)
  dayOfWeek?: number; // День недели (для WEEKLY)
};

function RuleWithSubtasks({
  rule,
  isEditing,
  template,
}: {
  rule: ChecklistItemRule;
  isEditing: boolean;
  template: ChecklistTemplate;
}) {
  const { hapticFeedback, showAlert, showConfirm } = useTelegram();
  const queryClient = useQueryClient();
  const [newSubtaskText, setNewSubtaskText] = useState('');
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [isEditingRule, setIsEditingRule] = useState(false);
  const getInitialRuleForm = (): RuleForm => {
    const endDate = rule.scheduleParams?.endDate || rule.duePolicy?.endDate;
    return {
      text: rule.text,
      recurrence: rule.recurrence,
      scheduleParams: rule.scheduleParams,
      duePolicy: rule.duePolicy,
      requiresApproval: !!rule.requiresApproval,
      startDate: rule.scheduleParams?.startDate,
      endDate: endDate,
      hasDate: rule.recurrence !== 'NO_DATE',
      isPermanent: rule.recurrence !== 'NO_DATE' && !endDate, // Постоянно, если есть дата начала, но нет даты окончания
      time: rule.scheduleParams?.time || '08:00',
      dayOfWeek: rule.scheduleParams?.dayOfWeek,
    };
  };
  const [ruleForm, setRuleForm] = useState<RuleForm>(getInitialRuleForm());

  useEffect(() => {
    // если правило обновилось с сервера — синхронизируем форму
    setRuleForm(getInitialRuleForm());
  }, [rule.id, rule.text, rule.recurrence, rule.scheduleParams, rule.duePolicy, rule.requiresApproval, rule.scheduleParams?.time, rule.scheduleParams?.dayOfWeek]);

  const { data: subtasks = [] } = useQuery<ChecklistItemSubtask[]>({
    queryKey: ['checklist-subtasks', rule.id],
    queryFn: () => checklistSubtasksApi.list(rule.id),
    enabled: !!rule.id,
  });

  const createSubtaskMutation = useMutation({
    mutationFn: (text: string) => checklistSubtasksApi.create(rule.id, { text }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist-subtasks', rule.id] });
      queryClient.invalidateQueries({ queryKey: ['checklist-template'] });
      setNewSubtaskText('');
      hapticFeedback('success');
      showAlert('Чек-пункт добавлен');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      showAlert(error.message || 'Не удалось добавить чек-пункт');
    },
  });

  const updateSubtaskMutation = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      checklistSubtasksApi.update(id, { text }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist-subtasks', rule.id] });
      setEditingSubtaskId(null);
      setEditingText('');
      hapticFeedback('success');
      showAlert('Чек-пункт обновлён');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      showAlert(error.message || 'Не удалось обновить чек-пункт');
    },
  });

  const deleteSubtaskMutation = useMutation({
    mutationFn: (id: string) => checklistSubtasksApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist-subtasks', rule.id] });
      hapticFeedback('success');
      showAlert('Чек-пункт удалён');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      showAlert(error.message || 'Не удалось удалить чек-пункт');
    },
  });

  const updateRuleMutation = useMutation({
    mutationFn: async (payload: RuleForm) => {
      if (!template?.id) throw new Error('Template ID is required');
      if (!payload.text.trim()) throw new Error('Укажите текст дела');

      const recurrence = payload.hasDate ? payload.recurrence : 'NO_DATE';
      const scheduleParams = payload.hasDate
        ? {
            ...(payload.scheduleParams || {}),
            ...(payload.startDate && { startDate: payload.startDate }),
            // Если "постоянно", не добавляем endDate
            ...(payload.endDate && !payload.isPermanent && { endDate: payload.endDate }),
            // Удаляем endDate из scheduleParams, если включено "постоянно"
            ...(payload.isPermanent && (() => {
              const { endDate, ...rest } = payload.scheduleParams || {};
              return rest;
            })()),
            // Сохраняем время и день недели
            ...(payload.time && { time: payload.time }),
            ...(payload.dayOfWeek !== undefined && { dayOfWeek: payload.dayOfWeek }),
          }
        : {};

      const duePolicy = payload.hasDate
        ? {
            ...(payload.duePolicy || { type: 'by_end_of_day' }),
            // Если "постоянно", не добавляем endDate в duePolicy
            ...(payload.endDate && !payload.isPermanent && { endDate: payload.endDate, type: 'by_date' }),
            // Если "постоянно", используем тип без даты
            ...(payload.isPermanent && { type: 'by_end_of_day' }),
          }
        : null;

      return checklistsApi.updateRule(template.id, rule.id, {
        text: payload.text.trim(),
        recurrence: recurrence as any,
        scheduleParams: Object.keys(scheduleParams).length > 0 ? scheduleParams : undefined,
        duePolicy: duePolicy as any,
        requiresApproval: payload.requiresApproval,
        startDate: payload.hasDate ? payload.startDate : undefined,
        // Если "постоянно", не отправляем endDate
        endDate: payload.hasDate && !payload.isPermanent ? payload.endDate : undefined,
      });
    },
    onSuccess: async () => {
      // Инвалидируем все связанные запросы (используем префикс для инвалидации всех вариантов)
      await queryClient.invalidateQueries({ queryKey: ['checklist-template'] });
      await queryClient.invalidateQueries({ queryKey: ['checklist-assignments'] });
      await queryClient.invalidateQueries({ queryKey: ['checklist-instances'] });
      await queryClient.invalidateQueries({ queryKey: ['checklist-instances-all'] });
      // Принудительно обновляем данные
      await queryClient.refetchQueries({ queryKey: ['checklist-template', template.id] });
      setIsEditingRule(false);
      hapticFeedback('success');
      showAlert('Дело обновлено');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      showAlert(error.message || 'Не удалось обновить дело');
    },
  });

  const deleteRuleMutation = useMutation({
    mutationFn: async () => {
      if (!template?.id) throw new Error('Template ID is required');
      console.log('[DeleteRule] Attempting to delete rule:', {
        templateId: template.id,
        ruleId: rule.id,
        recurrence: rule.recurrence,
        text: rule.text,
      });
      const response = await checklistsApi.deleteRule(template.id, rule.id);
      console.log('[DeleteRule] Delete API call completed:', response);
      return response;
    },
    onSuccess: async () => {
      console.log('[DeleteRule] Rule deleted successfully');
      // Инвалидируем все связанные запросы (используем префикс для инвалидации всех вариантов)
      await queryClient.invalidateQueries({ queryKey: ['checklist-template'] });
      await queryClient.invalidateQueries({ queryKey: ['checklist-assignments'] });
      await queryClient.invalidateQueries({ queryKey: ['checklist-instances'] });
      await queryClient.invalidateQueries({ queryKey: ['checklist-instances-all'] });
      // Принудительно обновляем данные
      await queryClient.refetchQueries({ queryKey: ['checklist-template', template.id] });
      hapticFeedback('success');
      showAlert('Дело удалено');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      console.error('[DeleteRule] Error:', {
        error,
        status: error.status,
        message: error.message,
        ruleId: rule.id,
        recurrence: rule.recurrence,
        templateId: template?.id,
      });
      // Показываем более понятное сообщение об ошибке
      const errorMessage = error.message || 'Не удалось удалить дело';
      
      if (error.status === 404) {
        showAlert('Дело не найдено');
      } else if (error.status === 403) {
        showAlert('Недостаточно прав для удаления дела');
      } else {
        showAlert(errorMessage);
      }
    },
  });

  const handleAddSubtask = () => {
    if (!newSubtaskText.trim()) {
      showAlert('Введите текст чек-пункта');
      return;
    }
    createSubtaskMutation.mutate(newSubtaskText.trim());
  };

  const handleEditSubtask = (subtask: ChecklistItemSubtask) => {
    setEditingSubtaskId(subtask.id);
    setEditingText(subtask.text);
  };

  const handleSaveEdit = (id: string) => {
    if (!editingText.trim()) {
      showAlert('Введите текст чек-пункта');
      return;
    }
    updateSubtaskMutation.mutate({ id, text: editingText.trim() });
  };

  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          {isEditingRule ? (
            <div className="space-y-3">
              <Input
                label="Текст дела"
                value={ruleForm.text}
                onChange={(e) => setRuleForm((s) => ({ ...s, text: e.target.value }))}
                placeholder="Введите текст дела"
              />

              {/* Опция "Без даты" для личных чек-листов */}
              {template.type === 'PERSONAL' && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`no-date-${rule.id}`}
                    checked={!ruleForm.hasDate}
                    onChange={(e) => {
                      const noDate = e.target.checked;
                      setRuleForm((s) => ({
                        ...s,
                        hasDate: !noDate,
                        recurrence: noDate ? 'NO_DATE' : (s.recurrence === 'NO_DATE' ? 'ONCE' : s.recurrence),
                      }));
                    }}
                    className="w-4 h-4 accent-blue-500"
                  />
                  <label htmlFor={`no-date-${rule.id}`} className="text-sm text-tg-text">
                    Без даты (входящие)
                  </label>
                </div>
              )}

              {ruleForm.hasDate && (
                <>
                  <div>
                    <label className="text-sm font-medium text-tg-text mb-2 block">Повторяемость</label>
                    <select
                      value={ruleForm.recurrence}
                      onChange={(e) => setRuleForm((s) => ({ ...s, recurrence: e.target.value as any }))}
                      className="w-full p-2 border rounded-lg text-sm"
                    >
                      {template.type === 'REGULATION' && (
                        <>
                          <option value="DAILY">Ежедневно</option>
                          <option value="WEEKLY">Еженедельно</option>
                          <option value="MONTHLY">Ежемесячно</option>
                        </>
                      )}
                      {template.type === 'EVENT' && <option value="ONCE">Разово</option>}
                      {template.type === 'PERSONAL' && (
                        <>
                          <option value="ONCE">Разово</option>
                          <option value="DAILY">Ежедневно</option>
                          <option value="WEEKLY">Еженедельно</option>
                          <option value="MONTHLY">Ежемесячно</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-tg-text mb-2 block">Дата начала</label>
                      <input
                        type="date"
                        value={ruleForm.startDate || format(startOfToday(), 'yyyy-MM-dd')}
                        onChange={(e) => setRuleForm((s) => ({ ...s, startDate: e.target.value }))}
                        className="w-full p-2 border rounded-lg text-sm"
                      />
                    </div>

                    {/* Поле времени для DAILY, WEEKLY, MONTHLY */}
                    {(ruleForm.recurrence === 'DAILY' || ruleForm.recurrence === 'WEEKLY' || ruleForm.recurrence === 'MONTHLY') && (
                      <div>
                        <label className="text-sm font-medium text-tg-text mb-2 block">Время выполнения</label>
                        <input
                          type="time"
                          value={ruleForm.time || '08:00'}
                          onChange={(e) => setRuleForm((s) => ({ ...s, time: e.target.value }))}
                          className="w-full p-2 border rounded-lg text-sm"
                        />
                      </div>
                    )}

                    {/* День недели для WEEKLY */}
                    {ruleForm.recurrence === 'WEEKLY' && (
                      <div>
                        <label className="text-sm font-medium text-tg-text mb-2 block">День недели</label>
                        <select
                          value={ruleForm.dayOfWeek ?? 1}
                          onChange={(e) => setRuleForm((s) => ({ ...s, dayOfWeek: Number(e.target.value) }))}
                          className="w-full p-2 border rounded-lg text-sm"
                        >
                          <option value={1}>Понедельник</option>
                          <option value={2}>Вторник</option>
                          <option value={3}>Среда</option>
                          <option value={4}>Четверг</option>
                          <option value={5}>Пятница</option>
                          <option value={6}>Суббота</option>
                          <option value={0}>Воскресенье</option>
                        </select>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`permanent-${rule.id}`}
                        checked={ruleForm.isPermanent || false}
                        onChange={(e) => {
                          const isPermanent = e.target.checked;
                          setRuleForm((s) => ({
                            ...s,
                            isPermanent,
                            endDate: isPermanent ? undefined : (s.endDate || s.startDate || format(startOfToday(), 'yyyy-MM-dd')),
                          }));
                        }}
                        className="w-4 h-4 accent-blue-500"
                      />
                      <label htmlFor={`permanent-${rule.id}`} className="text-sm text-tg-text">
                        Постоянно (без даты окончания)
                      </label>
                    </div>

                    {!ruleForm.isPermanent && (
                      <div>
                        <label className="text-sm font-medium text-tg-text mb-2 block">Дата окончания (дедлайн)</label>
                        <input
                          type="date"
                          value={ruleForm.endDate || ruleForm.startDate || format(startOfToday(), 'yyyy-MM-dd')}
                          min={ruleForm.startDate || format(startOfToday(), 'yyyy-MM-dd')}
                          onChange={(e) => setRuleForm((s) => ({ ...s, endDate: e.target.value }))}
                          className="w-full p-2 border rounded-lg text-sm"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`approval-${rule.id}`}
                  checked={ruleForm.requiresApproval}
                  onChange={(e) => setRuleForm((s) => ({ ...s, requiresApproval: e.target.checked }))}
                  className="w-4 h-4 accent-blue-500"
                />
                <label htmlFor={`approval-${rule.id}`} className="text-sm text-tg-text">
                  Требует приёмки
                </label>
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => updateRuleMutation.mutate(ruleForm)}
                  isLoading={updateRuleMutation.isPending}
                >
                  Сохранить
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setIsEditingRule(false);
                    setRuleForm(getInitialRuleForm());
                  }}
                >
                  Отмена
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="font-medium text-tg-text">{rule.text}</p>
              <div className="flex items-center gap-2 mt-1 text-xs text-tg-hint">
                <span>
                  {rule.recurrence === 'DAILY' && 'Ежедневно'}
                  {rule.recurrence === 'WEEKLY' && 'Еженедельно'}
                  {rule.recurrence === 'MONTHLY' && 'Ежемесячно'}
                  {rule.recurrence === 'ONCE' && 'Разово'}
                  {rule.recurrence === 'NO_DATE' && 'Без даты (входящие)'}
                </span>
                {rule.requiresApproval && <span className="text-blue-500">• Требует приёмки</span>}
              </div>
            </>
          )}
        </div>
        {isEditing && !isEditingRule && (
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => setIsEditingRule(true)} title="Изменить дело">
              <Pencil className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={async () => {
                hapticFeedback('medium');
                const confirmed = await showConfirm(
                  `Удалить дело "${rule.text}"?\n\nВсе задачи по этому делу также будут удалены.`
                );
                if (confirmed) {
                  deleteRuleMutation.mutate();
                }
              }}
              isLoading={deleteRuleMutation.isPending}
              title="Удалить дело"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>

      {/* Чек-пункты */}
      <div className="mt-3 space-y-2">
        <div className="text-xs font-medium text-tg-hint mb-2">Чек-пункты:</div>
        {subtasks.map((subtask) => (
          <div key={subtask.id} className="flex items-center gap-2 pl-4">
            {editingSubtaskId === subtask.id ? (
              <>
                <input
                  type="text"
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  className="flex-1 p-1.5 border rounded text-sm"
                  placeholder="Текст чек-пункта"
                />
                <Button
                  size="sm"
                  onClick={() => handleSaveEdit(subtask.id)}
                  isLoading={updateSubtaskMutation.isPending}
                >
                  Сохранить
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setEditingSubtaskId(null);
                    setEditingText('');
                  }}
                >
                  Отмена
                </Button>
              </>
            ) : (
              <>
                <span className="text-sm text-tg-text flex-1">• {subtask.text}</span>
                {isEditing && (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleEditSubtask(subtask)}
                    >
                      Изменить
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => deleteSubtaskMutation.mutate(subtask.id)}
                      isLoading={deleteSubtaskMutation.isPending}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        ))}
        {isEditing && (
          <div className="flex items-center gap-2 pl-4">
            <input
              type="text"
              value={newSubtaskText}
              onChange={(e) => setNewSubtaskText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddSubtask();
                }
              }}
              className="flex-1 p-1.5 border rounded text-sm"
              placeholder="Добавить чек-пункт..."
            />
            <Button
              size="sm"
              onClick={handleAddSubtask}
              isLoading={createSubtaskMutation.isPending}
            >
              <Plus className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function EditChecklistPage() {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const { hapticFeedback, showAlert } = useTelegram();
  const queryClient = useQueryClient();

  const { data: template, isLoading, error: templateError, refetch: refetchTemplate } = useQuery<ChecklistTemplate>({
    queryKey: ['checklist-template', templateId],
    queryFn: () => {
      if (!templateId) {
        throw new Error('Template ID is required');
      }
      return checklistsApi.get(templateId);
    },
    enabled: !!templateId,
    retry: (failureCount, error: any) => {
      // Не повторяем при ошибках авторизации или 404
      if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const [isEditing, setIsEditing] = useState(false);
  const [rules, setRules] = useState<RuleForm[]>([]);

  useEffect(() => {
    if (template?.rules) {
      setRules(
        template.rules.map((rule) => {
          const endDate = rule.scheduleParams?.endDate || rule.duePolicy?.endDate;
          return {
            text: rule.text,
            recurrence: rule.recurrence === 'NO_DATE' ? 'NO_DATE' : rule.recurrence,
            scheduleParams: rule.scheduleParams,
            duePolicy: rule.duePolicy,
            requiresApproval: rule.requiresApproval,
            startDate: rule.scheduleParams?.startDate,
            endDate: endDate,
            hasDate: rule.recurrence !== 'NO_DATE',
            isPermanent: rule.recurrence !== 'NO_DATE' && !endDate, // Постоянно, если есть дата начала, но нет даты окончания
            time: rule.scheduleParams?.time || '08:00',
            dayOfWeek: rule.scheduleParams?.dayOfWeek,
          };
        })
      );
    }
  }, [template]);

  const addRule = () => {
    if (!template) {
      showAlert('Чек-лист не загружен. Попробуйте обновить страницу.');
      return;
    }
    const today = startOfToday();
    // Для личных чек-листов: по умолчанию ONCE (разово) с датой
    // Пользователь может выбрать период (DAILY, WEEKLY, MONTHLY, ONCE) или NO_DATE (без даты)
    const defaultRecurrence = template.type === 'PERSONAL' ? 'ONCE' : 
                              template.type === 'REGULATION' ? 'DAILY' : 'ONCE';
    setRules([
      ...rules,
      {
        text: '',
        recurrence: defaultRecurrence,
        requiresApproval: false, // Для личных задач по умолчанию без приёмки
        startDate: format(today, 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd'), // Дата окончания по умолчанию = дата начала
        hasDate: true, // Для личных тоже можно задавать даты (период)
        isPermanent: false, // По умолчанию не постоянно
        time: '08:00', // Время по умолчанию
        dayOfWeek: 1, // Понедельник по умолчанию для WEEKLY
      },
    ]);
    hapticFeedback('light');
  };

  const removeRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
    hapticFeedback('light');
  };

  const updateRule = (index: number, field: keyof RuleForm, value: any) => {
    const updated = [...rules];
    updated[index] = { ...updated[index], [field]: value };
    setRules(updated);
  };

  const addRuleMutation = useMutation({
    mutationFn: async (rule: RuleForm) => {
      if (!templateId) throw new Error('Template ID is required');
      if (!rule.text.trim()) throw new Error('Укажите текст дела');
      
      // Определяем recurrence: если пользователь явно выбрал NO_DATE, используем его
      // Иначе используем выбранный период (DAILY, WEEKLY, MONTHLY, ONCE)
      const recurrence = rule.recurrence === 'NO_DATE' ? 'NO_DATE' : rule.recurrence;
      
      // Для задач без даты (NO_DATE) не нужны scheduleParams и duePolicy
      const scheduleParams = recurrence !== 'NO_DATE' ? {
        ...rule.scheduleParams,
        ...(rule.startDate && { startDate: rule.startDate }),
        // Если "постоянно", не добавляем endDate
        ...(rule.endDate && !rule.isPermanent && { endDate: rule.endDate }),
        // Сохраняем время и день недели
        ...(rule.time && { time: rule.time }),
        ...(rule.dayOfWeek !== undefined && { dayOfWeek: rule.dayOfWeek }),
      } : {};
      
      // Если указана дата окончания, используем её в duePolicy
      const duePolicy = recurrence !== 'NO_DATE' ? {
        ...(rule.duePolicy || { type: 'by_end_of_day' }),
        // Если "постоянно", не добавляем endDate в duePolicy
        ...(rule.endDate && !rule.isPermanent && { endDate: rule.endDate, type: 'by_date' }),
        // Если "постоянно", используем тип без даты
        ...(rule.isPermanent && { type: 'by_end_of_day' }),
      } : null;
      
      return checklistsApi.addRule(templateId, {
        text: rule.text.trim(),
        recurrence: recurrence as 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'ONCE' | 'NO_DATE',
        scheduleParams: Object.keys(scheduleParams).length > 0 ? scheduleParams : undefined,
        duePolicy: duePolicy || undefined,
        requiresApproval: rule.requiresApproval,
        startDate: recurrence !== 'NO_DATE' ? rule.startDate : undefined,
      });
    },
    onSuccess: async () => {
      // Инвалидируем все связанные запросы (используем префикс для инвалидации всех вариантов)
      await queryClient.invalidateQueries({ queryKey: ['checklist-template'] });
      await queryClient.invalidateQueries({ queryKey: ['checklist-assignments'] });
      await queryClient.invalidateQueries({ queryKey: ['checklist-instances'] });
      await queryClient.invalidateQueries({ queryKey: ['checklist-instances-all'] });
      // Принудительно обновляем данные
      await queryClient.refetchQueries({ queryKey: ['checklist-template', templateId] });
      hapticFeedback('success');
      showAlert('Дело добавлено!');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      showAlert(error.message || 'Не удалось добавить дело');
    },
  });

  const handleSaveRule = async (index: number) => {
    if (!templateId) {
      showAlert('Ошибка: ID чек-листа не найден');
      return;
    }
    const rule = rules[index];
    if (!rule) {
      showAlert('Ошибка: правило не найдено');
      return;
    }
    if (!rule.text.trim()) {
      showAlert('Укажите текст дела');
      return;
    }
    // Защита от двойного вызова
    if (addRuleMutation.isPending) {
      return;
    }
    try {
      await addRuleMutation.mutateAsync(rule);
      // Удаляем правило из формы после успешного сохранения
      removeRule(index);
    } catch (error: any) {
      // Ошибка уже обработана в onError мутации
      console.error('Error saving rule:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-tg-hint">Загрузка...</div>
      </div>
    );
  }

  if (templateError) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500 mb-4">
          {(templateError as any)?.message || 'Ошибка загрузки чек-листа'}
        </p>
        <div className="flex gap-2 justify-center">
          <Button onClick={() => refetchTemplate()}>Повторить</Button>
          <Button variant="secondary" onClick={() => navigate('/checklists')}>Назад</Button>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500 mb-4">Чек-лист не найден</p>
        <Button onClick={() => navigate('/checklists')}>Назад</Button>
      </div>
    );
  }

  return (
    <div className="pb-24">
      {/* Заголовок */}
      <div className="p-4 bg-tg-secondary-bg sticky top-0 z-10">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => {
              hapticFeedback('light');
              navigate('/checklists');
            }}
            className="text-blue-500"
          >
            ← Назад
          </button>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => {
                hapticFeedback('light');
                addRule();
              }}
              className="bg-blue-500 text-white hover:bg-blue-600"
            >
              <Plus className="w-4 h-4 mr-1" />
              Добавить дело
            </Button>
            <button
              onClick={() => {
                hapticFeedback('light');
                setIsEditing(!isEditing);
              }}
              className={`p-2 rounded-lg transition-colors ${
                isEditing 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              title={isEditing ? 'Завершить редактирование' : 'Редактировать'}
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
        <h1 className="text-xl font-bold text-tg-text">{template.title}</h1>
        {template.description && (
          <p className="text-sm text-tg-hint mt-1">{template.description}</p>
        )}
        {isEditing && (
          <p className="text-xs text-blue-500 mt-2">Режим редактирования</p>
        )}
      </div>

      <div className="px-4 space-y-4">
        {/* Существующие правила */}
        {template.rules && template.rules.length > 0 && (
          <Card className="p-4">
            <h2 className="text-lg font-semibold text-tg-text mb-3">Существующие дела</h2>
            <div className="space-y-2">
              {template.rules.map((rule, index) => (
                <RuleWithSubtasks key={rule.id} rule={rule} isEditing={isEditing} template={template} />
              ))}
            </div>
          </Card>
        )}

        {/* Добавление нового дела - всегда доступно */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-tg-text">Добавить дело</h2>
            {rules.length > 0 && (
              <Button size="sm" variant="secondary" onClick={addRule}>
                <Plus className="w-4 h-4 mr-1" />
                Добавить ещё
              </Button>
            )}
          </div>

          <div className="space-y-4">
            {rules.map((rule, index) => {
              // Сквозная нумерация: существующие правила + текущий индекс
              const globalIndex = (template.rules?.length || 0) + index + 1;
              return (
              <Card key={index} className="p-4 bg-gray-50">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-sm font-medium text-tg-text">Дело {globalIndex}</span>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => removeRule(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-3">
                  <Input
                    label="Текст дела"
                    value={rule.text}
                    onChange={(e) => updateRule(index, 'text', e.target.value)}
                    placeholder="Введите текст дела"
                  />

                  {/* Опция "Без даты" для личных чек-листов */}
                  {template.type === 'PERSONAL' && (
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        id={`no-date-${index}`}
                        checked={!rule.hasDate}
                        onChange={(e) => {
                          updateRule(index, 'hasDate', !e.target.checked);
                          if (!e.target.checked) {
                            updateRule(index, 'recurrence', 'NO_DATE');
                          } else {
                            updateRule(index, 'recurrence', 'ONCE');
                          }
                        }}
                        className="w-4 h-4 accent-blue-500"
                      />
                      <label htmlFor={`no-date-${index}`} className="text-sm text-tg-text">
                        Без даты (входящие)
                      </label>
                    </div>
                  )}

                  {rule.hasDate && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-tg-text mb-2 block">
                          Повторяемость
                        </label>
                        <select
                          value={rule.recurrence}
                          onChange={(e) => updateRule(index, 'recurrence', e.target.value)}
                          className="w-full p-2 border rounded-lg text-sm"
                        >
                          {template.type === 'REGULATION' && (
                            <>
                              <option value="DAILY">Ежедневно</option>
                              <option value="WEEKLY">Еженедельно</option>
                              <option value="MONTHLY">Ежемесячно</option>
                            </>
                          )}
                          {template.type === 'EVENT' && (
                            <option value="ONCE">Разово</option>
                          )}
                          {template.type === 'PERSONAL' && (
                            <>
                              <option value="ONCE">Разово</option>
                              <option value="DAILY">Ежедневно</option>
                              <option value="WEEKLY">Еженедельно</option>
                              <option value="MONTHLY">Ежемесячно</option>
                            </>
                          )}
                        </select>
                      </div>

                      {rule.recurrence !== 'ONCE' && rule.recurrence !== 'NO_DATE' && (
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-tg-text mb-2 block">
                              Дата начала
                            </label>
                            <input
                              type="date"
                              value={rule.startDate || format(startOfToday(), 'yyyy-MM-dd')}
                              onChange={(e) => updateRule(index, 'startDate', e.target.value)}
                              className="w-full p-2 border rounded-lg text-sm"
                            />
                          </div>

                          {/* Поле времени для DAILY, WEEKLY, MONTHLY */}
                          {(rule.recurrence === 'DAILY' || rule.recurrence === 'WEEKLY' || rule.recurrence === 'MONTHLY') && (
                            <div>
                              <label className="text-sm font-medium text-tg-text mb-2 block">
                                Время выполнения
                              </label>
                              <input
                                type="time"
                                value={rule.time || '08:00'}
                                onChange={(e) => updateRule(index, 'time', e.target.value)}
                                className="w-full p-2 border rounded-lg text-sm"
                              />
                            </div>
                          )}

                          {/* День недели для WEEKLY */}
                          {rule.recurrence === 'WEEKLY' && (
                            <div>
                              <label className="text-sm font-medium text-tg-text mb-2 block">
                                День недели
                              </label>
                              <select
                                value={rule.dayOfWeek ?? 1}
                                onChange={(e) => updateRule(index, 'dayOfWeek', Number(e.target.value))}
                                className="w-full p-2 border rounded-lg text-sm"
                              >
                                <option value={1}>Понедельник</option>
                                <option value={2}>Вторник</option>
                                <option value={3}>Среда</option>
                                <option value={4}>Четверг</option>
                                <option value={5}>Пятница</option>
                                <option value={6}>Суббота</option>
                                <option value={0}>Воскресенье</option>
                              </select>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`permanent-new-${index}`}
                              checked={rule.isPermanent || false}
                              onChange={(e) => {
                                const isPermanent = e.target.checked;
                                updateRule(index, 'isPermanent', isPermanent);
                                if (!isPermanent) {
                                  updateRule(index, 'endDate', rule.endDate || rule.startDate || format(startOfToday(), 'yyyy-MM-dd'));
                                }
                              }}
                              className="w-4 h-4 accent-blue-500"
                            />
                            <label htmlFor={`permanent-new-${index}`} className="text-sm text-tg-text">
                              Постоянно (без даты окончания)
                            </label>
                          </div>

                          {!rule.isPermanent && (
                            <div>
                              <label className="text-sm font-medium text-tg-text mb-2 block">
                                Дата окончания (дедлайн)
                              </label>
                              <input
                                type="date"
                                value={rule.endDate || rule.startDate || format(startOfToday(), 'yyyy-MM-dd')}
                                onChange={(e) => updateRule(index, 'endDate', e.target.value)}
                                min={rule.startDate || format(startOfToday(), 'yyyy-MM-dd')}
                                className="w-full p-2 border rounded-lg text-sm"
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {rule.recurrence === 'ONCE' && (
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-tg-text mb-2 block">
                              Дата начала
                            </label>
                            <input
                              type="date"
                              value={rule.startDate || format(startOfToday(), 'yyyy-MM-dd')}
                              onChange={(e) => updateRule(index, 'startDate', e.target.value)}
                              className="w-full p-2 border rounded-lg text-sm"
                            />
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`permanent-once-${index}`}
                              checked={rule.isPermanent || false}
                              onChange={(e) => {
                                const isPermanent = e.target.checked;
                                updateRule(index, 'isPermanent', isPermanent);
                                if (!isPermanent) {
                                  updateRule(index, 'endDate', rule.endDate || rule.startDate || format(startOfToday(), 'yyyy-MM-dd'));
                                }
                              }}
                              className="w-4 h-4 accent-blue-500"
                            />
                            <label htmlFor={`permanent-once-${index}`} className="text-sm text-tg-text">
                              Постоянно (без даты окончания)
                            </label>
                          </div>

                          {!rule.isPermanent && (
                            <div>
                              <label className="text-sm font-medium text-tg-text mb-2 block">
                                Дата окончания (дедлайн)
                              </label>
                              <input
                                type="date"
                                value={rule.endDate || rule.startDate || format(startOfToday(), 'yyyy-MM-dd')}
                                onChange={(e) => updateRule(index, 'endDate', e.target.value)}
                                min={rule.startDate || format(startOfToday(), 'yyyy-MM-dd')}
                                className="w-full p-2 border rounded-lg text-sm"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`approval-${index}`}
                      checked={rule.requiresApproval}
                      onChange={(e) => updateRule(index, 'requiresApproval', e.target.checked)}
                      className="w-4 h-4 accent-blue-500"
                    />
                    <label htmlFor={`approval-${index}`} className="text-sm text-tg-text">
                      Требует приёмки
                    </label>
                  </div>

                  <Button
                    onClick={() => handleSaveRule(index)}
                    isLoading={addRuleMutation.isPending}
                    className="w-full"
                  >
                    Сохранить дело
                  </Button>
                </div>
              </Card>
              );
            })}

            {rules.length === 0 && (
              <div className="text-center py-8 text-tg-hint">
                <CheckSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Нажмите "Добавить дело" вверху чтобы создать новое дело</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}


